import { useEffect, useRef } from 'react';

import type { GridApi, GridReadyEvent } from 'ag-grid-enterprise';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';

import { appConfig } from '@app/config/appConfig';
import { AG_GRID_CONFIG } from '@shared/components/ui/custom/ag-grid/utils/constants';
import { GridOverlay } from '@shared/overlays/error-overlays/GridOverlay';
import { oiTheme, oiErrorTheme } from '@shared/theme/theme-ag-grid';
import { cn } from '@shared/utils';

export interface AgGridServerSideProps extends AgGridReactProps {
  hideDisabledContextMenuActions?: boolean;
  loadingText?: string;
  noRowsText?: string;
  errorText?: string;
  'data-testid'?: string;
  pageSize?: number;
  isError?: boolean;
  context?: Record<string, unknown>;
}

const DEFAULT_COLUMNS = {
  wrapHeaderText: true,
  autoHeaderHeight: true,
  suppressHeaderMenuButton: true,
  resizable: false,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    padding: AG_GRID_CONFIG.DEFAULT_CELL_PADDING,
  },
};

export const ServerSideTableWrapper = ({
  hideDisabledContextMenuActions,
  loadingText,
  noRowsText,
  'data-testid': testId,
  pageSize,
  isError,
  errorText,
  context,
  ...rest
}: AgGridServerSideProps) => {
  const gridApiRef = useRef<GridApi | null>(null);

  useEffect(() => {
    const api = gridApiRef.current;

    if (!api) return;

    if (isError) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  }, [isError]);

  const handleGridReady = (event: GridReadyEvent) => {
    gridApiRef.current = event.api;

    rest.onGridReady?.(event);
  };

  const refetch = () => {
    gridApiRef.current?.refreshServerSide({
      purge: true,
    });
  };

  const theme = isError ? oiErrorTheme : oiTheme;

  return (
    <div className="flex flex-1 min-h-0">
      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 w-full',
          hideDisabledContextMenuActions && 'hide-disabled-context-menus',
        )}
        data-testid={testId}
      >
        <AgGridReact
          {...rest}
          theme={theme}
          rowModelType="serverSide"
          onGridReady={handleGridReady}
          columnDefs={rest.columnDefs ?? []}
          defaultColDef={{
            ...DEFAULT_COLUMNS,
            ...(isError && { loadingCellRenderer: () => null }),
          }}
          context={{
            ...context,
            isError,
            errorMessage: errorText,
            refetch,
            noRowsMessage: noRowsText,
          }}
          suppressServerSideFullWidthLoadingRow
          noRowsOverlayComponent={GridOverlay}
          cacheBlockSize={pageSize ?? AG_GRID_CONFIG.SSRM_DEFAULT_PAGINATION_PAGE_SIZE}
          blockLoadDebounceMillis={AG_GRID_CONFIG.BLOCK_LOAD_DEBOUNCE_MILLIS}
          cacheOverflowSize={AG_GRID_CONFIG.CACHE_OVERFLOW_SIZE}
          maxConcurrentDatasourceRequests={AG_GRID_CONFIG.MAX_CONCURRENT_DATASOURCE_REQUESTS}
          suppressCellFocus
          rowBuffer={AG_GRID_CONFIG.SSRM_ROW_BUFFER}
          gridOptions={{ styleNonce: appConfig.cspNonce }}
          enableCellExpressions={false}
        />
      </div>
    </div>
  );
};
